import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Grid from '@mui/material/Grid';
import ArticleIcon from '@mui/icons-material/Article';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import 'moment/locale/pt-br';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../hooks/useAuth';
import { useQuery } from '../hooks/useQuery';
import { useSnackbar } from '../hooks/useSnackbar';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { handleHttpError } from '../utils';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import CustomizedDialogs from '../components/ModalAlert';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { RowDocumentFornecedor } from '../components/RowDocumentFornecedor';

const MATRIZ_DOCUMENTOS_ENVIO = {
  acima_microempresa: {
    or: [12, 1],
    conditional: {},
    documents: [2, 3, 4, 5],
  },
  acima_mei: {
    or: [],
    conditional: {},
    documents: [8, 9, 2, 3, 4, 5],
  },
  acima_empresa_de_pequeno_porte: {
    or: [12, 1],
    conditional: {},
    documents: [2, 3, 4, 5],
  },
  acima_entidades_sem_fins_lucrativos: {
    or: [],
    conditional: { 10: [11] },
    documents: [2, 3, 4, 5],
  },
  acima_outras: {
    or: [1, 7, 10],
    conditional: { 10: [11] },
    documents: [2, 3, 4, 5, 6],
  },
  abaixo_microempresa: {
    or: [12, 1, 7],
    conditional: {},
    documents: [],
  },
  abaixo_empresa_de_pequeno_porte: {
    or: [12, 1, 7],
    conditional: {},
    documents: [],
  },
  abaixo_entidades_sem_fins_lucrativos: {
    or: [],
    conditional: { 10: [11] },
    documents: [],
  },
  abaixo_outras: {
    or: [1, 7, 10],
    conditional: { 10: [11] },
    documents: [6],
  },
  abaixo_mei: {
    or: [],
    conditional: {},
    documents: [8],
  },
  contrato_sem_repasse_financeiro: {
    or: [],
    conditional: { 10: [11] },
    documents: [9, 1],
  },
};

const DOC_NAMES = {
  1: 'Contrato Social Consolidado da Empresa',
  2: 'Certidão Negativa de Débitos Federais (CND Federal)',
  3: 'Certidão Negativa de Débitos Estaduais (CND Estadual)',
  4: 'Certidão Negativa de Débitos Municipais (CND Municipal)',
  5: 'Certificado de Regularidade do FGTS',
  6: 'Documento de vínculo (declaração, contrato, carteira de trabalho, eSocial, contracheque)',
  7: 'Requerimento do empresário individual (ME ou EPP)',
  8: 'Certificado da Condição de Microempreendedor Individual (CCMEI)',
  9: 'Procuração',
  10: 'Ata de Nomeação dos Representantes Legais',
  11: 'Estatuto Social da Empresa',
  12: 'Comprovante de Inscrição e Situação Cadastral do CNPJ',
};

export const CertidoesPage = ({
  activeStep,
  setActiveStep,
  fornecedor,
  setFornecedor,
}) => {
  const theme = useTheme();
  const snackbar = useSnackbar();

  const [loadingFlluig, setLoadingFluig] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [checked, setChecked] = React.useState(false);
  const [justificativa, setJustificativa] = React.useState(
    fornecedor.justificativa || '',
  );
  const { isUsuarioSebrae } = useAuth();

  const {
    listarArquivosFornecedor,
    enviarCadastroFluig,
    enviarJustificativaFornecedor,
    showCadastroFornecedor,
  } = useQuery();
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [type, setType] = React.useState('');
  const [show, setShow] = React.useState(false);
  const [showDialogError, setShowDialogError] = React.useState(false);
  const [messageError, setMessageError] = React.useState('');
  const [ordensErro, setOrdensErro] = React.useState([]);
  const [arquivosEnviados, setArquivosEnviados] = React.useState([]);
  const [isHtml, setIsHtml] = React.useState(false);
  const navigate = useNavigate();
  const [colaboradores, setColaboradores] = React.useState([]);

  const key =
    fornecedor.contratacao_teto && fornecedor.declaracao_enquadramento
      ? `${fornecedor.contratacao_teto}_${fornecedor.declaracao_enquadramento
          .split(' ')
          .join('_')}`.toLowerCase()
      : '';

  const getDocumentosParaEnviar = () => {
    if (!MATRIZ_DOCUMENTOS_ENVIO[key]) return [];

    if (colaboradores.length > 1) {
      MATRIZ_DOCUMENTOS_ENVIO[key].or = [
        ...new Set([...MATRIZ_DOCUMENTOS_ENVIO[key].or, 9]),
      ];

      MATRIZ_DOCUMENTOS_ENVIO[key].documents = [
        ...new Set([...MATRIZ_DOCUMENTOS_ENVIO[key].documents, 6]),
      ];
    }

    const mapDocuments = MATRIZ_DOCUMENTOS_ENVIO[key];
    const documentosTipos = [];
    Object.entries(mapDocuments.conditional).forEach(([k, v]) => {
      documentosTipos.push(Number(k));
      documentosTipos.push(...v.map((item) => Number(item)));
    });
    documentosTipos.push(...mapDocuments.or);
    documentosTipos.push(...mapDocuments.documents);

    // if (fornecedor.forma_recebimento === 'contrato_sem_repasse_financeiro') {
    //   if (fornecedor.declaracao_enquadramento === 'MEI') {
    //     MATRIZ_DOCUMENTOS_ENVIO.contrato_sem_repasse_financeiro.or = [8];
    //   } else {
    //     MATRIZ_DOCUMENTOS_ENVIO.contrato_sem_repasse_financeiro.or = [7, 1];
    //   }
    // }

    return [...new Set(documentosTipos)]
      .map((id) => {
        const arquivoEncontrado = arquivosEnviados.find((doc) => {
          return Number(doc.tipo) === id;
        });
        return {
          title: DOC_NAMES[id],
          type: id,
          fileUploaded: arquivoEncontrado || null,
        };
      })
      .sort((a, b) => {
        const getPrioridade = (n) => {
          if (n.type === 8) return 0; // Prioridade máxima
          if ([2, 3, 4].includes(n.type)) return 1; // Segunda prioridade
          return 2; // Restante
        };

        return getPrioridade(a) - getPrioridade(b);
      });
  };

  const documentos = useMemo(() => {
    return getDocumentosParaEnviar();
  }, [fornecedor, arquivosEnviados, colaboradores]);

  const validarEnvioDocumentos = () => {
    let errors = [];

    if (!MATRIZ_DOCUMENTOS_ENVIO[key])
      return {
        valido: true,
        errors,
      };

    const mapDocuments = MATRIZ_DOCUMENTOS_ENVIO[key];

    const hasOne = mapDocuments.or.some((type) => {
      return arquivosEnviados.find((doc) => {
        return Number(doc.tipo) === Number(type);
      });
    });

    if (!hasOne) {
      errors.push(...mapDocuments.or);
    }

    mapDocuments.documents.forEach((type) => {
      const encontrado = arquivosEnviados.find((doc) => {
        return Number(doc.tipo) === type;
      });

      if (!encontrado) {
        errors.push(type);
      }
    });

    Object.entries(mapDocuments.conditional).forEach(([k, v]) => {
      const encontrado = arquivosEnviados.find((doc) => {
        return Number(doc.tipo) === Number(k);
      });

      if (encontrado) {
        v.forEach((id) => {
          const arquivoCondicionalAdicionado = arquivosEnviados.find((doc) => {
            return Number(doc.tipo) === id;
          });
          if (!arquivoCondicionalAdicionado) {
            errors.push(id);
          }
        });
      }
    });

    // if (fornecedor.forma_recebimento === 'contrato_sem_repasse_financeiro') {
    //   MATRIZ_DOCUMENTOS_ENVIO.contrato_sem_repasse_financeiro.documents.forEach(
    //     (type) => {
    //       const encontrado = arquivosEnviados.find((doc) => {
    //         return Number(doc.tipo) === type;
    //       });

    //       if (!encontrado) {
    //         errors.push(type);
    //       }
    //     },
    //   );

    //   Object.entries(
    //     MATRIZ_DOCUMENTOS_ENVIO.contrato_sem_repasse_financeiro.conditional,
    //   ).forEach(([k, v]) => {
    //     const encontrado = arquivosEnviados.find((doc) => {
    //       return Number(doc.tipo) === Number(k);
    //     });

    //     if (encontrado) {
    //       v.forEach((id) => {
    //         const arquivoCondicionalAdicionado = arquivosEnviados.find(
    //           (doc) => {
    //             return Number(doc.tipo) === id;
    //           },
    //         );

    //         if (!arquivoCondicionalAdicionado) {
    //           errors.push(id);
    //         }
    //       });
    //     }
    //   });

    //   const hasOne =
    //     MATRIZ_DOCUMENTOS_ENVIO.contrato_sem_repasse_financeiro.or.some(
    //       (type) => {
    //         return arquivosEnviados.find((doc) => {
    //           return Number(doc.tipo) === type;
    //         });
    //       },
    //     );

    //   if (!hasOne) {
    //     errors.push(...mapDocuments.or);
    //   }
    // }

    /**
     * Se tiver a Procuração na lista de documentos necessários, o mesmo não precisa ser enviado, caso todos os outros seja enviado
     */
    if (errors.length === 1 && errors.includes(9)) {
      errors = errors.filter((item) => item !== 9);
    }

    return {
      valido: errors.length === 0,
      errors: errors,
    };
  };

  React.useEffect(() => {
    if (fornecedor.tipo !== 'PF') {
      buscarDados();
    }
  }, []);

  const clickDetalheModal = () => {
    setMessage(ordensErro && ordensErro.length > 1 && ordensErro[1]);
    setOrdensErro([]);
    setIsHtml(false);
    setType('error');
    setShow(true);
  };

  const onBlurJustificativa = async () => {
    try {
      setIsSaving(true);
      await enviarJustificativaFornecedor(fornecedor.id, {
        justificativa,
      });
    } catch (error) {
      snackbar.showSnackbar(handleHttpError(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    setLoadingFluig(true);
    try {
      let response = await enviarCadastroFluig(fornecedor.id);

      if (
        response &&
        response.data &&
        response.data.response &&
        response.data.response.item &&
        response.data.response.item.item &&
        response.data.response.item.item[0] &&
        response.data.response.item.item[0] == 'ERROR'
      ) {
        setMessageError(response.data.response.item.item[1]);
        setShowDialogError(true);
        setLoadingFluig(false);
      } else {
        setMessage('Cadastro enviado com sucesso.');
        setType('success');
        setShow(true);
        setLoadingFluig(false);
      }
    } catch (err) {
      setMessage(handleHttpError(err));
      setType('Error');
      setShow(true);
      setLoadingFluig(false);
    }
  };

  const buscarDados = async () => {
    try {
      setLoading(true);
      const result = await listarArquivosFornecedor(fornecedor.id);
      setArquivosEnviados(result.data.data);
      const cadastro = await showCadastroFornecedor(fornecedor.id);
      setColaboradores(cadastro.data.data.colaboradores);
    } catch (error) {
      snackbar.showSnackbar(error.toString());
    } finally {
      setLoading(false);
    }
  };

  const onClickFinish = async () => {
    if (fornecedor.tipo === 'PJ') {
      const temDocumentsSemDatas = arquivosEnviados.filter(
        (doc) => doc.data_emissao === null && doc.data_validade === null,
      );

      if (temDocumentsSemDatas.length > 0) {
        setMessage('Preencha as datas dos documentos enviados');
        setOrdensErro([]);
        setIsHtml(false);
        setType('error');
        setShow(true);
        return;
      }

      const result = validarEnvioDocumentos();

      if (!result.valido && !justificativa) {
        const titleDocuments = result.errors.map((item) => {
          return DOC_NAMES[item];
        });

        setMessage(
          `Faça upload dos documento(s): ${titleDocuments.join(
            ', ',
          )} \n ou adicione uma justificativa da ausência dos mesmos.`,
        );
        setOrdensErro([]);
        setIsHtml(false);
        setType('error');
        setShow(true);
        return;
      }
    }

    handleNext();
  };

  const handleClose = () => {
    setShow(false);
  };

  const handleButtonOk = () => {
    setShow(false);
    navigate('/dashboard/listar-cadastros');
  };

  const handleCheck = (event) => {
    setChecked(event.target.checked);
  };

  return (
    <Box sx={{ width: '100%', background: '#FAFCFF', minHeight: '100vh' }}>
      <CustomizedDialogs
        content={messageError}
        btnTxt={'Ok'}
        type="error"
        onClick={() => setShowDialogError(false)}
        handleClose={setShowDialogError}
        visible={showDialogError}
        ordens={ordensErro}
        isHtml={isHtml}
        onClickDetalhe={clickDetalheModal}
      />
      <CustomizedDialogs
        content={message}
        btnTxt={'Ok'}
        type={type}
        onClick={type == 'Error' ? () => setShow(false) : handleButtonOk}
        handleClose={handleClose}
        visible={show}
      />
      <AppBar position="static" color="primary" style={{ padding: 10 }}>
        <Grid container spacing={0}>
          <Grid item xs={0.5}>
            <ArticleIcon
              sx={{ fontSize: 40, display: { xs: 'none', md: 'flex' }, mr: 1 }}
            />
          </Grid>
          <Grid item xs={11.5} alignItems="center" display="flex">
            <h3 style={{ color: 'white', margin: 0 }}>
              Documentos / Certidões
            </h3>
          </Grid>
        </Grid>
      </AppBar>

      <Grid container spacing={1} paddingY="2rem" paddingX="1rem">
        {fornecedor.tipo !== 'PF' && (
          <Grid item sm={12} style={{ marginTop: 5 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress style={{ marginTop: 60 }} />
              </Box>
            ) : (
              <>
                <table className="app_table">
                  <thead
                    style={{
                      color: theme.palette.primary.main,
                      borderTop: `8px solid ${theme.palette.primary.light}`,
                    }}
                  >
                    <tr>
                      <th>Código</th>
                      <th>Descrição</th>
                      <th>Status</th>
                      <th>Dt. Emissão</th>
                      <th>Dt. Validade</th>
                      <th>Observação</th>
                      <th></th>
                    </tr>
                  </thead>

                  <tbody>
                    {documentos.map((item, index) => {
                      return (
                        <RowDocumentFornecedor
                          title={item.title}
                          type={item.type}
                          number={String(item.type).padStart(2, '0')}
                          fornecedor_id={fornecedor.id}
                          onUploaded={buscarDados}
                          fileUploaded={item.fileUploaded}
                        />
                      );
                    })}
                  </tbody>
                </table>
                <label
                  htmlFor="justificativa"
                  style={{
                    marginTop: '1rem',
                    display: 'block',
                  }}
                >
                  Justifique a ausência de documentação (Certidões obrigatórias
                  para fornecedores credenciados, licitados ou cuja contratação
                  ultrapasse o valor de R$ 104.000,00 mil reais)
                </label>
                <textarea
                  id="justificativa"
                  cols="10"
                  rows="10"
                  placeholder="Digite sua justificativa"
                  value={justificativa}
                  onBlur={onBlurJustificativa}
                  onChange={({ target }) => setJustificativa(target.value)}
                  style={{
                    resize: 'none',
                    width: '100%',
                    borderRadius: '0.2rem',
                    border: '1px solid #919191',
                    marginBottom: '1rem',
                  }}
                />
              </>
            )}
          </Grid>
        )}
        <Grid item sm={12}>
          <Stack sx={{ width: '100%' }} spacing={2}>
            <Alert severity="success">
              Para fins de cadastro para prestação de serviços ao SEBRAE/MG, não
              poderão se cadastrar pessoas jurídicas que tenham na composição de
              seu capital social ou em seu quadro social/diretivo ex-dirigentes
              e/ou ex-empregados do SEBRAE/MG, até 18 (dezoito) meses, contados
              do seu desligamento, demissão ou do pedido de demissão, exceto se
              os referidos titulares forem aposentados.
            </Alert>
            <Alert severity="success">
              Todos os documentos deverão estar dentro do prazo de sua(s)
              respectiva(s) validade(s)..
            </Alert>
            <Alert severity="success">
              A pessoas jurídica interessada em se cadastrar deverá apresentar
              conta bancária própria (conta corrente)
            </Alert>
            <Alert severity="success">
              Vencido o prazo de 180 (cento e oitenta) dias, a pessoa jurídica
              deverá providenciar a renovação do seu cadastro junto ao
              SEBRAE/MG, sob pena de não liberação de eventuais pagamentos e/ou
              repasses, assim como de não celebração de novos
              contratos/parcerias até a sua efetiva renovação.
            </Alert>
            <Alert severity="success">
              Compete exclusivamente à pessoa jurídica cadastrada acompanhar o
              prazo de vigência do cadastro, bem como providenciar a sua
              renovação, ficando o SEBRAE/MG isento de emitir qualquer
              comunicado prévio acerca do seu vencimento.
            </Alert>
            <Alert severity="success">
              Independente da vigência do cadastro, caso seja comprovada a
              existência de declarações/documentação inverídicas, a pessoa
              jurídica cadastrada terá o seu cadastro automaticamente
              suspenso/bloqueado, sem prejuízo da aplicação das penalidades
              incidentes para o caso concreto.
            </Alert>
          </Stack>
        </Grid>
        <Grid item sm={12}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checked}
                  onChange={handleCheck}
                  style={{ padding: '4px' }}
                />
              }
              label={
                'Confirmo que todas as informações inseridas no sistema são verdadeiras.'
              }
            />
          </FormGroup>
          <hr style={{ marginTop: 30 }} />
        </Grid>
      </Grid>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          pt: 2,
          justifyContent: 'space-between',
          marginBottom: '-50px',
        }}
      >
        <Button
          type="button"
          variant="text"
          color="primary"
          style={{ marginRight: '0.5rem' }}
          onClick={() => {
            setActiveStep(activeStep - 1);
          }}
        >
          <ArrowBackIosIcon color="primary" />
          Voltar
        </Button>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            pt: 2,
            justifyContent: 'flex-end',
          }}
        >
          <Button
            type="button"
            variant="contained"
            color="warning"
            style={{ marginRight: '0.5rem' }}
            onClick={() => {
              snackbar.showSnackbar('Rascunho salvo com sucesso.');
              navigate('/dashboard/listar-cadastros');
            }}
          >
            Salvar como rascunho
            <SaveIcon />
          </Button>
          <Button
            type="button"
            variant="contained"
            disabled={!checked || isSaving}
            onClick={onClickFinish}
          >
            Finalizar
            {loadingFlluig || isSaving ? (
              <CircularProgress size={24} color="secondary" />
            ) : null}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
